import React, { ReactNode, useState } from "react";
import { Button, CardAction } from "@cofano/cck";
import {
	IconButton,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Box,
	Typography,
	TypographyProps,
	AccordionProps,
	AccordionDetailsProps,
	useTheme,
	Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useConfirm } from "components/hooks/useConfirm";
import "./styling/styling.scss";

export const FormArrayContainer = ({ onAdd, children, padding = 16 }: {
	onAdd?: (event: React.MouseEvent<HTMLElement>) => void;
	children: React.ReactNode;
	padding?: number;
}) => {
	const theme = useTheme();
	return (
		<div style={{ backgroundColor: theme.palette.mode === "dark" ? "inherit" : "#F0F0F0", padding: padding }}>
			{children}

			{onAdd && (
				<div>
					<Button icon={<AddIcon />}
						onClick={onAdd}
						size="small"
						variant="outlined" />
				</div>
			)}
		</div>
	);
};

export const FormArrayRow = ({ title, subtitle, onDuplicate, onDelete, canDelete = true, collapsable, onCustomActions, collapsed, children, color, disablePadding = false, collapseOnlyOnIcon = false, slotProps = {} }: {
	title: React.ReactNode,
	subtitle?: string | React.ReactNode,
	onDelete?: () => void,
	canDelete?: boolean,
	onDuplicate?: () => void,
	onCustomActions?: CardAction[]
	collapsed?: boolean,
	collapsable?: boolean,
	color?: string,
	children: React.ReactNode,
	disablePadding?: boolean,
	collapseOnlyOnIcon?: boolean,
	slotProps?: {
		root?: Omit<AccordionProps, "children">,
		title?: Omit<TypographyProps, "children">,
		subtitle?: Omit<TypographyProps, "children">,
		content?: Omit<AccordionDetailsProps, "children">,
	}
}) => {
	const isCollapsable = collapsable !== undefined ? collapsable : true;
	const [ isExpanded, setIsExpanded ] = useState(!collapsed);

	const actions: CardAction[] = [];

	if (onDelete) {
		actions.push({
			onClick: onDelete,
			icon: <DeleteIcon />,
			disabled: !canDelete,
		});
	}

	if (onDuplicate) {
		actions.push({
			onClick: onDuplicate,
			icon: <ContentCopyIcon fontSize="small" />,
		});
	}

	if (onCustomActions) {
		actions.push(...onCustomActions);
	}

	const hasHeaderContent = !!title || !!subtitle || actions.length > 0;

	return (
		<Accordion
			variant="outlined"
			expanded={!isCollapsable || isExpanded}
			square
			disableGutters
			onChange={() => !collapseOnlyOnIcon && isCollapsable && setIsExpanded(!isExpanded)}
			{...slotProps.root}
			sx={{
				borderLeft: color ? "unset" : undefined,
				boxShadow: color ? `-4px 0 0 ${color}` : undefined,
				marginBottom: 2,
				"&::before": {
					display: "none",
				},
				...slotProps.root?.sx,
			}}
			slotProps={{
				transition: { unmountOnExit: true },
				...slotProps.root?.slotProps,
			}}
		>
			{hasHeaderContent && (
				<AccordionSummary
					expandIcon={isCollapsable ? (
						<ExpandMoreIcon
							fontSize="small"
							color="primary"
							onClick={collapseOnlyOnIcon ? (event) => {
								// stop the click from reaching the header's onClick
								event.stopPropagation();
								setIsExpanded(!isExpanded);
							} : undefined}
						/>
					) : null}
					slotProps={{
						root: {
							sx: {
								cursor: isCollapsable ? undefined : "default !important",
								"&.Mui-expanded": {
									minHeight: "48px",
								},
							},
						},
						content: {
							sx: {
								"&.Mui-expanded": {
									marginY: "12px",
								},
								justifyContent: "space-between",
							},
						},
					}}
				>
					<Box
						display="flex"
						alignItems="flex-start"
						justifyContent="center"
						flexDirection="column"
						flexGrow={1}
						paddingRight={1}
					>
						<Typography
							textTransform="uppercase"
							fontWeight="bold"
							fontSize={14}
							component="div"
							alignContent="center"
							display="flex"
							width="100%"
							flexGrow={1}
							{...slotProps.title}
						>
							{title}
						</Typography>
						{subtitle && (
							<Typography
								flexGrow={1}
								fontSize={13}
								variant="subtitle1"
								color="text.secondary"
								{...slotProps.subtitle}
							>
								{subtitle}
							</Typography>
						)}
					</Box>
					<Box
						display="flex"
						alignItems="center"
						sx={{ marginRight: 1 }}
						onClick={(e) => e.stopPropagation()}
					>
						{actions.map((action: CardAction, index) => (
							<Box component="span" marginLeft={0.5} key={index}>
								{action.render ? action.render() : (
									<Tooltip title={action.tooltip || ""}>
										<IconButton
											size="small"
											onClick={action.onClick}
											disabled={action.disabled}
											color="primary"
										>
											{action.icon && React.cloneElement(action.icon, { fontSize: "small" })}
										</IconButton>
									</Tooltip>
								)}
							</Box>
						))}
					</Box>
				</AccordionSummary>
			)}
			<AccordionDetails
				{...slotProps.content}
				sx={{
					borderTop: "1px solid rgba(0, 0, 0, 0.12)",
					padding: disablePadding ? 0 : undefined,
					...slotProps.content?.sx,
				}}
			>
				{children}
			</AccordionDetails>
		</Accordion>
	);
};

type FormArrayOnlyRowsField = {
	title: React.ReactNode,
	render: (index: number) => React.ReactNode,
	required?: boolean
}


export const FormArrayOnlyRows = ({ array, fields, optionalRows, onAdd, onDelete }: {
	array: { [key in "id" | "generatedId"]?: number | string }[],
	fields: FormArrayOnlyRowsField[],
	optionalRows?: ReactNode;
	onAdd?: () => void,
	onDelete?: (index: number) => void
}) => {

	const confirm = useConfirm();

	return (
		<div className="form-array-only-rows">
			<table>
				<thead>
					<tr>
						{fields.map((column, index) => <th key={index}>
							{column.title}
							<span className={`required-indicator ${column.required ? "required" : ""}`}> *</span>
						</th>)}
						{onDelete && <th />}
					</tr>
				</thead>

				<tbody>
					{array.map((item, index) => {
						return <tr key={item.id || item.generatedId}>
							{fields.map((field, fieldIndex) => (
								<td className="item" key={fieldIndex}>
									{field.render(index)}
								</td>
							))}

							{onDelete && (
								<td>
									<IconButton
										size="small"
										onClick={() => {
											confirm({
												type: "DELETE",
											}).then(() => {
												onDelete(index);
											});
										}}>
										<DeleteIcon fontSize="small" />
									</IconButton>
								</td>
							)}
						</tr>;
					})}

					{optionalRows}
				</tbody>
			</table>

			{onAdd && (
				<div style={{ marginTop: 8 }}>
					<Button
						icon={<AddIcon />}
						onClick={onAdd}
						size="small"
						variant="outlined" />
				</div>
			)}
		</div>
	);
};
